import { useEffect, useMemo, useState } from 'react';
import { aws } from '../lib/awsdb';
import { authWrite } from '../lib/authWrite';
import { downloadXlsx } from '../lib/exportXlsx';
import { logExportActivity } from '../lib/exportActivityLog';
import OnHandDrawer from './OnHandDrawer';
import AnimatedNumber from '../ui/AnimatedNumber';
import { Button } from '../ui/button';
import { IconDownload } from '../ui/icons';
import '../po/drawer.css';

/* ── helpers (unchanged) ── */
function today() { return new Date(); }
function addDays(d, n) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }

function derive4WeekAvg(weeklyData, skuId) {
  const sorted = weeklyData.filter(w => w.sku_id === skuId).sort((a, b) => b.week_start.localeCompare(a.week_start));
  const last4 = sorted.slice(0, 4);
  if (last4.length === 0) return 0;
  const totalUnits = last4.reduce((s, w) => s + Number(w.units_sold || 0), 0);
  return Math.round((totalUnits / last4.length) * 4.33);
}

/*
 * Walk the stock curve: starting from SOH, subtract dailySales over time,
 * adding incoming arrivals at their expected dates. Returns:
 *   stockoutDate  — when stock hits zero (factoring in all arrivals)
 *   flatStockout  — when stock would hit zero WITHOUT any incoming
 *   effectiveCoverDays — total days of cover including incoming
 */
function walkStockCurve(soh, dailySales, incomingEntries) {
  const flatDays = dailySales > 0 ? Math.floor(soh / dailySales) : null;
  const flatStockout = flatDays != null ? addDays(today(), flatDays) : null;

  if (dailySales <= 0 || incomingEntries.length === 0) {
    return { stockoutDate: flatStockout, flatStockout, effectiveCoverDays: flatDays };
  }

  const sorted = [...incomingEntries].sort((a, b) => a.expected_date.localeCompare(b.expected_date));
  let stock = soh;
  let cursor = today();

  for (const inc of sorted) {
    const arrivalDate = new Date(inc.expected_date);
    if (arrivalDate <= cursor) { stock += Number(inc.qty); continue; }
    const daysUntilArrival = Math.floor((arrivalDate - cursor) / 86400000);
    const consumed = dailySales * daysUntilArrival;
    if (consumed >= stock) {
      const daysFromCursor = Math.floor(stock / dailySales);
      const stockoutDate = addDays(cursor, daysFromCursor);
      const effectiveCoverDays = Math.floor((stockoutDate - today()) / 86400000);
      return { stockoutDate, flatStockout, effectiveCoverDays };
    }
    stock = stock - consumed + Number(inc.qty);
    cursor = arrivalDate;
  }

  const remainingDays = Math.floor(stock / dailySales);
  const stockoutDate = addDays(cursor, remainingDays);
  const effectiveCoverDays = Math.floor((stockoutDate - today()) / 86400000);
  return { stockoutDate, flatStockout, effectiveCoverDays };
}

/*
 * Walk the effective stock curve to find any zero-stock gap.
 * If stock hits zero and there's still a pending arrival after that point,
 * return the number of days between runs-out and next arrival.
 */
function computeStockoutGap(soh, dailySales, futureIncoming) {
  if (dailySales <= 0 || futureIncoming.length === 0) return null;
  const sorted = [...futureIncoming].sort((a, b) => a.expected_date.localeCompare(b.expected_date));
  let stock = soh;
  let cursor = today();

  for (let i = 0; i < sorted.length; i++) {
    const arrivalDate = new Date(sorted[i].expected_date);
    if (arrivalDate <= cursor) { stock += Number(sorted[i].qty); continue; }
    const daysToArrival = Math.floor((arrivalDate - cursor) / 86400000);
    const consumed = dailySales * daysToArrival;
    if (consumed >= stock) {
      // Stock hits zero before this arrival
      const daysUntilZero = Math.floor(stock / dailySales);
      const zeroDate = addDays(cursor, daysUntilZero);
      const gapDays = Math.floor((arrivalDate - zeroDate) / 86400000);
      return gapDays > 0 ? gapDays : null;
    }
    stock = stock - consumed + Number(sorted[i].qty);
    cursor = arrivalDate;
  }
  // Stock never hit zero before any arrival — no gap
  return null;
}

/*
 * Determine one of 5 status states:
 *   healthy    — effective cover exceeds target weeks
 *   order_soon — effective cover inside reorder window, no incoming booked
 *   order_now  — effective cover below reorder point, no incoming booked
 *   gap        — incoming booked but lands AFTER current stockout (gap of X days)
 *   partial    — incoming arrives on time but doesn't cover reorder need
 */
function deriveStatus(daysCover, effectiveCoverDays, leadDays, targetWeeks, hasIncoming, flatStockout, stockoutDate, dailySales, incoming) {
  const targetDays = targetWeeks * 7;

  if (effectiveCoverDays >= targetDays) return { status: 'healthy', label: 'Healthy' };

  if (hasIncoming) {
    // Check for gap: does stock hit zero before first useful arrival?
    if (flatStockout && dailySales > 0) {
      const flatStockoutTime = flatStockout.getTime();
      // Find earliest incoming that actually adds stock after today
      const futureIncoming = incoming.filter(i => new Date(i.expected_date) > today());
      if (futureIncoming.length > 0) {
        const firstArrival = new Date(futureIncoming[0].expected_date);
        if (firstArrival > flatStockout) {
          const gapDays = Math.floor((firstArrival - flatStockout) / 86400000);
          if (gapDays > 0) return { status: 'gap', label: `Gap ${gapDays}d`, gapDays };
        }
      }
    }
    // Incoming arrives on time — check if it's enough
    if (effectiveCoverDays < targetDays) return { status: 'partial', label: 'Partial' };
    return { status: 'healthy', label: 'Healthy' };
  }

  // No incoming
  if (daysCover < leadDays) return { status: 'order_now', label: 'Order Now' };
  if (daysCover < leadDays + 14) return { status: 'order_soon', label: 'Order Soon' };
  return { status: 'healthy', label: 'Healthy' };
}

function compute(row, monthlyAvg, incoming) {
  const soh = Number(row.stock_on_hand || 0);
  const msa = monthlyAvg;
  const dailySales = msa / 30;
  const leadDays = Number(row.lead_time_days || 84);
  const targetWeeks = Number(row.target_cover_weeks || 12);
  if (msa === 0) return { daysCover: null, reorderDays: null, stockoutDate: null, status: 'none', statusLabel: 'No Data', monthlyAvg: 0, dailySales: 0, effectiveCoverDays: null };
  const daysCover = Math.floor(soh / dailySales);
  const { stockoutDate, flatStockout, effectiveCoverDays } = walkStockCurve(soh, dailySales, incoming);
  const effectiveReorderDays = effectiveCoverDays != null ? effectiveCoverDays - leadDays : daysCover - leadDays;
  const { status, label, gapDays } = deriveStatus(daysCover, effectiveCoverDays, leadDays, targetWeeks, incoming.length > 0, flatStockout, stockoutDate, dailySales, incoming);
  return {
    daysCover, monthlyAvg: msa, dailySales,
    reorderDays: effectiveReorderDays,
    stockoutDate, status, statusLabel: label, gapDays,
    effectiveCoverDays
  };
}

// Packaging vocabulary (migration 101 renamed `category` → `uom`).
// We group On-Hand by UoM. "Other" is a tail bucket for rows where
// uom hasn't been set yet so they still render — staff can see what
// to fix instead of having those SKUs disappear from the page.
const UOM_ORDER = ['Sachet Boxes', 'Pouches', 'Tins', 'Bottles', 'Jars', 'Boxes', 'Rewind', 'Accessories', 'Raw Materials', 'Other'];

function fmtDDMMYY(d) {
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
}

function freshnessLabel(hours) {
  if (hours == null) return { text: '—', color: '#a1a1aa', bg: 'transparent' };
  // Format: 0h-23h, 1d-6d, 1w-3w, 1m-11m, 1y+
  let text;
  if (hours < 24) text = `${hours}h`;
  else if (hours < 24 * 7) text = `${Math.floor(hours / 24)}d`;
  else if (hours < 24 * 30) text = `${Math.floor(hours / (24 * 7))}w`;
  else if (hours < 24 * 365) text = `${Math.floor(hours / (24 * 30))}m`;
  else text = `${Math.floor(hours / (24 * 365))}y`;
  // Colour: muted for <24h, amber 1-7d, red 8-14d, bold red 15d+
  if (hours < 24) return { text, color: '#a1a1aa', bg: 'transparent' };
  if (hours < 24 * 3) return { text, color: '#a1a1aa', bg: 'transparent' };
  if (hours < 24 * 7) return { text, color: '#92400E', bg: '#FEF0C7' };
  if (hours < 24 * 14) return { text, color: '#dc2626', bg: '#FEE4E2' };
  return { text, color: '#fff', bg: '#dc2626', bold: true };
}

function logFc(session, action, detail) {
  return aws.from('forecaster_activity').insert([{ actor_name: session?.name, action, detail }]);
}

/* ── SOH / Days Cover colour ── */
function coverColor(daysCover) {
  if (daysCover == null) return undefined;
  if (daysCover < 14) return '#dc2626';   // < 2 weeks
  if (daysCover < 42) return '#d97706';   // < 6 weeks
  return '#16a34a';
}

export default function Hub({
  session, skus, catalog, weeklyData, incomingData,
  loadSkus, loadCatalog, loadIncoming,
  openSkuId, onCloseDrawer,
}) {
  // Same stock_write flag Catalogue gates writes on — download is a
  // write-adjacent (export) action, so it follows the same rule.
  const canWrite = session?.role === 'admin' || session?.permissions?.stock_write !== false;
  const [skuPane, setSkuPane] = useState(null);

  // External open: when the parent (Forecaster.jsx) hands us an openSkuId
  // — typically because a "Needs attention" card on the Dashboard was
  // clicked — find the matching row from `skus` and open the drawer.
  useEffect(() => {
    if (!openSkuId) return;
    const row = skus.find(s => s.id === openSkuId);
    if (row) setSkuPane(row);
  }, [openSkuId, skus]);

  function closeDrawer() {
    setSkuPane(null);
    onCloseDrawer?.();
  }

  /* ── Global 4-week units for MIX calculation ── */
  const global4wUnits = useMemo(() => {
    const now = today();
    const fourWeeksAgo = addDays(now, -28);
    const cutoff = fourWeeksAgo.toISOString().slice(0, 10);
    return weeklyData.filter(w => w.week_start >= cutoff).reduce((s, w) => s + Number(w.units_sold || 0), 0);
  }, [weeklyData]);

  /* ── Rows with computed values + catalog join ── */
  const sortedRows = useMemo(() => {
    const now = today();
    // Latest weekly sales entry per SKU
    const weeklyLatest = {};
    weeklyData.forEach(w => {
      const ts = w.created_at || w.week_start;
      if (!weeklyLatest[w.sku_id] || ts > weeklyLatest[w.sku_id]) weeklyLatest[w.sku_id] = ts;
    });

    return [...skus].map(r => {
      const derived = derive4WeekAvg(weeklyData, r.id);
      const msa = r.monthly_avg_override != null ? Number(r.monthly_avg_override) : derived;
      const catItem = catalog.find(c => c.id === r.catalog_id);
      // Most recent of: SKU record updated_at, catalog updated_at, or latest weekly entry
      const timestamps = [weeklyLatest[r.id], r.updated_at, catItem?.updated_at].filter(Boolean);
      const latest = timestamps.length > 0 ? timestamps.sort().pop() : null;
      const hoursSince = latest != null ? Math.floor((now - new Date(latest)) / (1000 * 60 * 60)) : null;
      const skuIncoming = incomingData.filter(i => i.sku_id === r.id);
      const c = compute(r, msa, skuIncoming);
      const soh = Number(r.stock_on_hand || 0);
      // TREND: 4-week avg vs prior 4-week avg (MoM change)
      const skuWeekly = weeklyData.filter(w => w.sku_id === r.id).sort((a, b) => b.week_start.localeCompare(a.week_start));
      const recent4 = skuWeekly.slice(0, 4).filter(w => w.units_sold != null);
      const prior4 = skuWeekly.slice(4, 8).filter(w => w.units_sold != null);
      let trend = null;
      if (recent4.length >= 2 && prior4.length >= 2) {
        const recentAvg = recent4.reduce((s, w) => s + Number(w.units_sold), 0) / recent4.length;
        const priorAvg = prior4.reduce((s, w) => s + Number(w.units_sold), 0) / prior4.length;
        if (priorAvg > 0) trend = Math.round(((recentAvg - priorAvg) / priorAvg) * 100);
      }
      // Future arrivals with days from today
      const futureInc = skuIncoming.filter(i => new Date(i.expected_date) >= now);
      // DOH: (SOH + total incoming) / daily velocity
      const totalIncomingQty = futureInc.reduce((s, i) => s + Number(i.qty || 0), 0);
      const doh = (c.dailySales > 0 && (soh + totalIncomingQty) > 0) ? Math.floor((soh + totalIncomingQty) / c.dailySales) : null;
      const futureArrivals = futureInc.map(i => ({
        days: Math.max(0, Math.floor((new Date(i.expected_date) - now) / 86400000)),
        qty: Number(i.qty),
      }));
      // Stockout dry days: walk the curve to find any zero-stock gap before a later arrival
      const dryDays = computeStockoutGap(soh, c.dailySales, futureInc);
      // VALUE: SOH × unit cost
      const unitCost = Number(catItem?.unit_cost || 0);
      const invValue = soh * unitCost;
      // MIX: this SKU's 4-week units as % of total
      const fourWeeksAgo = addDays(now, -28).toISOString().slice(0, 10);
      const sku4wUnits = weeklyData.filter(w => w.sku_id === r.id && w.week_start >= fourWeeksAgo).reduce((s, w) => s + Number(w.units_sold || 0), 0);
      const mix = global4wUnits > 0 ? (sku4wUnits / global4wUnits) * 100 : null;
      return { ...r, _c: c, _derived: derived, _cat: catItem, _hoursSinceUpdate: hoursSince, _trend: trend, _doh: doh, _futureArrivals: futureArrivals, _dryDays: dryDays, _value: invValue, _mix: mix };
    }).sort((a, b) => {
      // Sort by dry days descending (worst first), then by effective cover ascending
      const aDry = a._dryDays ?? -1;
      const bDry = b._dryDays ?? -1;
      if (aDry !== bDry) return bDry - aDry;
      const aCover = a._c.effectiveCoverDays ?? 9999;
      const bCover = b._c.effectiveCoverDays ?? 9999;
      return aCover - bCover;
    });
  }, [skus, weeklyData, catalog, incomingData]);

  /* ── Group by UoM (packaging) ── */
  const groupedRows = useMemo(() => {
    const g = {};
    UOM_ORDER.forEach(u => { g[u] = []; });
    sortedRows.forEach(row => {
      const u = row._cat?.uom || 'Other';
      if (!g[u]) g[u] = [];
      g[u].push(row);
    });
    return g;
  }, [sortedRows]);

  /* ── Inbound summary ── */
  const inboundSummary = useMemo(() => {
    let totalUnits = 0, totalShipments = 0;
    sortedRows.forEach(r => { r._futureArrivals.forEach(a => { totalUnits += a.qty; totalShipments++; }); });
    return { totalUnits, totalShipments };
  }, [sortedRows]);

  // Flat (not grouped-by-UoM) export — one row per SKU is what's actually
  // pivot/sum-friendly once opened in a spreadsheet.
  function handleDownloadSoh() {
    const columns = [
      { label: 'UoM', width: 14, value: r => r._cat?.uom || 'Other' },
      { label: 'Code', width: 12, value: r => r._cat?.sku_code || '' },
      { label: 'Name', width: 30, value: r => r.sku_name },
      { label: 'SOH (units)', width: 12, numFmt: '#,##0', value: r => Number(r.stock_on_hand || 0) },
      { label: 'Value (AUD)', width: 14, numFmt: '"A$"#,##0', value: r => {
        const unitCost = Number(r._cat?.unit_cost || 0);
        const soh = Number(r.stock_on_hand || 0);
        return unitCost > 0 && soh > 0 ? Math.round(soh * unitCost) : 0;
      } },
      { label: 'Avg/Month (units)', width: 16, numFmt: '#,##0', value: r => r._c.status !== 'none' ? r._c.monthlyAvg : '' },
      { label: 'Trend (%)', width: 10, value: r => r._trend ?? '' },
      { label: 'Mix (%)', width: 10, numFmt: '0.0', value: r => r._mix != null ? Number(r._mix.toFixed(1)) : '' },
      { label: 'DOH (days)', width: 12, value: r => r._doh ?? '' },
      { label: 'Stockout gap (days)', width: 16, value: r => r._dryDays ?? '' },
      { label: 'Runs Out', width: 12, value: r => r._doh != null ? addDays(today(), r._doh).toISOString().slice(0, 10) : '' },
      { label: 'Freshness (hrs)', width: 14, value: r => r._hoursSinceUpdate ?? '' },
    ];
    const filename = `FON_Stock_On_Hand_${today().toISOString().slice(0, 10)}.xlsx`;
    downloadXlsx(filename, 'Stock On Hand', columns, sortedRows);
    logExportActivity(session, 'Stock On Hand', filename);
  }

  return (
    <>
      {/* On-Hand polish overrides — scoped to .onhand-tbl so other
          fc-table surfaces (Catalogue, Demand) stay untouched. Tunes
          typography, row breathing room, dim-secondary columns, and
          softens the stockout red per Mo's "calm + structured" brief. */}
      <style>{`
        .onhand-tbl { font-feature-settings: "tnum"; }
        .onhand-tbl .fc-th {
          padding: 12px 14px;
          font-size: 10.5px;
          letter-spacing: 0.12em;
          color: rgba(0,0,0,0.42);
          background: rgba(255,255,255,0.35);
        }
        .onhand-tbl .fc-td {
          padding: 14px 14px;
          line-height: 1.35;
        }
        .onhand-tbl .fc-name {
          font-size: 14.5px;
          font-weight: 600;
          color: #0F1419;
          letter-spacing: -0.005em;
          max-width: 240px;
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .onhand-tbl .fc-code {
          font-size: 11.5px;
          color: #9A9DA1;
          letter-spacing: 0.02em;
        }
        .onhand-tbl .fc-soh {
          font-size: 14.5px;
          font-weight: 600;
          color: #0F1419;
        }
        .onhand-tbl .fc-doh {
          font-size: 13.5px;
          font-weight: 500;
          color: #0F1419;
        }
        .onhand-tbl .fc-runs-out {
          font-size: 12.5px;
          font-weight: 500;
          color: #0F1419;
          letter-spacing: -0.005em;
          font-variant-numeric: tabular-nums;
        }
        .onhand-tbl .fc-arriving {
          font-size: 12.5px;
          color: #2A2F35;
        }
        .onhand-tbl .fc-value,
        .onhand-tbl .fc-avg {
          font-size: 12.5px;
          color: #6E7278;
          font-weight: 500;
        }
        /* Secondary columns — Trend / Mix / Fresh — visually quieter */
        .onhand-tbl .fc-secondary { opacity: 0.78; }
        .onhand-tbl .fc-secondary.fc-num { color: #6E7278; }
        /* Dashes — intentional, not missing */
        .onhand-tbl .fc-dash { color: #C8C7C2; font-weight: 400; }
        /* Stockout pill — softer red, same urgency cue */
        .onhand-tbl .fc-stockout-pill {
          background: #FEE2E2;
          color: #B91C1C;
          font-weight: 600;
          font-size: 10.5px;
          padding: 2px 9px;
          border-radius: 999px;
          letter-spacing: 0.02em;
        }
        /* Runs-out date — unify warm/red tones */
        .onhand-tbl .fc-runs-soon { color: #C2410C; }
        .onhand-tbl .fc-runs-crit { color: #B91C1C; }
        /* Trend pill — keep semantic green/red but slightly less loud */
        .onhand-tbl .fc-trend-up   { background: rgba(22,163,74,0.08);  color: #15803D; }
        .onhand-tbl .fc-trend-down { background: rgba(220,38,38,0.08);  color: #B91C1C; }
        .onhand-tbl .fc-trend-pill {
          display: inline-flex; align-items: center; gap: 2px;
          border-radius: 6px; padding: 1px 7px;
          font-size: 11px; font-weight: 600; line-height: 1;
          font-variant-numeric: tabular-nums;
        }
        /* Container softness */
        .onhand-tbl-wrap {
          border-radius: 18px;
          border-color: rgba(255,255,255,0.7);
          box-shadow: 0 2px 14px rgba(15,20,25,0.04), 0 1px 0 rgba(255,255,255,0.85) inset;
        }
        /* Section header — slightly stronger UOM label, more space above */
        .onhand-group { margin-top: 28px; }
        .onhand-group:first-child { margin-top: 8px; }
        .onhand-group .fc-group-header { margin-bottom: 12px; }
        .onhand-group .fc-group-label {
          font-size: 10.5px;
          letter-spacing: 0.14em;
          color: #2A2F35;
          background: rgba(255,255,255,0.7);
          border-color: rgba(0,0,0,0.06);
        }
        .onhand-group .fc-group-count {
          font-size: 10.5px;
          color: #9A9DA1;
        }
        /* Hover — calm */
        .onhand-tbl tr.fc-row-hover { transition: background 140ms ease; }
        .onhand-tbl tr.fc-row-hover:hover { background: rgba(15,20,25,0.025); cursor: pointer; }
      `}</style>

      {canWrite && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
          <Button kind="ghost" icon={<IconDownload />} onClick={handleDownloadSoh}>Download SOH (Excel)</Button>
        </div>
      )}

      {/* Grouped tables */}
      {UOM_ORDER.map(uom => {
        const rows = groupedRows[uom];
        if (!rows || rows.length === 0) return null;
        return (
          <div key={uom} className="fc-group onhand-group">
            <div className="fc-group-header">
              <span className="fc-group-label">{uom}</span>
              <span className="fc-group-count">{rows.length}</span>
            </div>
            <div className="fc-table-wrap onhand-tbl-wrap" style={{ overflowX: 'auto' }}>
              <table className="fc-table onhand-tbl" style={{ minWidth: 1000 }}>
                <thead>
                  <tr>
                    <th className="fc-th" style={{ width: 85 }}>Code</th>
                    <th className="fc-th" style={{ width: 140 }}>Name</th>
                    <th className="fc-th fc-num" style={{ width: 55 }}>SOH</th>
                    <th className="fc-th" style={{ width: 90 }}>Arriving</th>
                    <th className="fc-th fc-num" style={{ width: 70 }}>Value</th>
                    <th className="fc-th fc-num" style={{ width: 55 }}>Avg/Mth</th>
                    <th className="fc-th fc-num" style={{ width: 50 }}>Trend</th>
                    <th className="fc-th fc-num" style={{ width: 45 }}>Mix</th>
                    <th className="fc-th fc-num" style={{ width: 45 }}>DOH</th>
                    <th className="fc-th fc-num" style={{ width: 60 }}>Stockout</th>
                    <th className="fc-th" style={{ width: 70 }}>Runs Out</th>
                    <th className="fc-th" style={{ width: 50 }}>Fresh</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(row => {
                    const c = row._c;
                    const hasData = c.status !== 'none';
                    const unitCost = Number(row._cat?.unit_cost || 0);
                    const soh = Number(row.stock_on_hand || 0);
                    const value = unitCost > 0 && soh > 0 ? soh * unitCost : null;
                    const doh = row._doh;
                    const runsOutDate = doh != null ? addDays(today(), doh) : null;
                    const runsOutStr = runsOutDate ? runsOutDate.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' }) : null;
                    const runsOutColor = doh != null ? (doh <= 14 ? '#dc2626' : doh <= 30 ? '#d97706' : undefined) : undefined;
                    const fr = freshnessLabel(row._hoursSinceUpdate);
                    const dash = <span className="fc-dash">—</span>;
                    const runsOutClass = doh != null && doh <= 14 ? 'fc-runs-crit' : doh != null && doh <= 30 ? 'fc-runs-soon' : '';
                    return (
                      <tr key={row.id} className="fc-row-hover fc-clickable" onClick={() => setSkuPane(row)}>
                        <td className="fc-td fc-code">{row._cat?.sku_code || dash}</td>
                        <td className="fc-td fc-name" title={row.sku_name}>{row.sku_name}</td>
                        <td className="fc-td fc-num fc-soh">{row.stock_on_hand != null ? <AnimatedNumber value={row.stock_on_hand} /> : dash}</td>
                        <td className="fc-td fc-arriving">
                          {row._futureArrivals.length === 0 ? dash : (
                            <span>{row._futureArrivals[0].days}d · {row._futureArrivals[0].qty.toLocaleString()}u</span>
                          )}
                        </td>
                        <td className="fc-td fc-num fc-value">{value != null ? `$${Math.round(value).toLocaleString()}` : dash}</td>
                        <td className="fc-td fc-num fc-avg">{hasData && c.monthlyAvg > 0 ? c.monthlyAvg.toLocaleString() : dash}</td>
                        <td className="fc-td fc-num fc-secondary">
                          {row._trend != null && row._trend !== 0 ? (
                            <span className={`fc-trend-pill ${row._trend > 0 ? 'fc-trend-up' : 'fc-trend-down'}`}>{row._trend > 0 ? '↑' : '↓'}{Math.abs(row._trend)}%</span>
                          ) : dash}
                        </td>
                        <td className="fc-td fc-num fc-secondary">{row._mix != null ? `${row._mix.toFixed(1)}%` : dash}</td>
                        <td className="fc-td fc-num fc-doh">{doh != null ? `${doh}d` : dash}</td>
                        <td className="fc-td fc-num" style={{ textAlign: 'center' }}>
                          {row._dryDays != null ? (
                            <span className="fc-stockout-pill">{row._dryDays}d</span>
                          ) : dash}
                        </td>
                        <td className={`fc-td fc-runs-out ${runsOutClass}`}>{runsOutStr || dash}</td>
                        <td className="fc-td fc-secondary" style={{ textAlign: 'right' }}>
                          <span style={{ fontSize: 11, color: fr.color, fontVariantNumeric: 'tabular-nums' }}>{fr.text}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {sortedRows.length === 0 && <div className="fc-empty" style={{ padding: 48 }}>No active SKUs in catalog.</div>}

      {/* On-Hand drawer (replaces the legacy SkuDetailPane for this context) */}
      {skuPane && (
        <OnHandDrawer
          session={session}
          sku={skuPane}
          catalog={catalog}
          weeklyData={weeklyData}
          incomingData={incomingData}
          onClose={closeDrawer}
          onSaved={() => { loadSkus?.(); loadCatalog?.(); loadIncoming?.(); }}
        />
      )}
    </>
  );
}
